import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, Button, Input, Label } from "./ui";
import SearchableSelect from "./SearchableSelect";
import VendorSelect from "./VendorSelect";
import NumberInput from "./NumberInput";
import { fmtIDR, errMsg } from "../lib/utils";
import { toast } from "sonner";
import { X, Plus, Trash2, FilePlus2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyRow = () => ({ awb_id: "", vendor_name: "", amount: "", description: "" });

// Catat sisa biaya belum dibayar (mis. 30%) atas panjar yang sudah settle → hutang vendor
export default function PanjarRemainderModal({ panjar, orders, onClose, onSaved }) {
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([emptyRow()]);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadVendors = () => api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  useEffect(() => { loadVendors(); }, []);

  const panjarAwbIds = new Set((panjar.settle_history || []).flatMap((h) => (h.breakdown || []).map((b) => b.awb_id)).filter(Boolean));
  const options = [...orders]
    .sort((a, b) => panjarAwbIds.has(b.id) - panjarAwbIds.has(a.id))
    .map((o) => ({ value: o.id, label: o.awb_number, sub: `${panjarAwbIds.has(o.id) ? "★ AWB panjar ini · " : ""}${o.customer_name || ""} · ${o.origin}→${o.destination}` }));
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const submit = async () => {
    const items = rows.filter((r) => parseFloat(r.amount) > 0);
    if (!items.length) return toast.error("Isi minimal 1 baris dengan nominal");
    if (items.some((r) => !r.awb_id || !r.vendor_name)) return toast.error("AWB dan vendor wajib diisi tiap baris");
    if (!date) return toast.error("Tanggal wajib diisi");
    setSaving(true);
    try {
      const { data } = await api.post(`/cash-advances/${panjar.id}/unpaid-remainder`, {
        date, items: items.map((r) => ({ ...r, amount: parseFloat(r.amount) })),
      });
      toast.success(`Sisa hutang ${fmtIDR(total)} tercatat — lunasi lewat baris "Pelunasan Hutang" di panjar berikutnya`);
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[60] flex items-center justify-center p-4" data-testid="panjar-remainder-modal">
      <Card className="w-full max-w-4xl p-6 min-h-[480px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading font-bold text-xl inline-flex items-center gap-2"><FilePlus2 size={18} className="text-amber-600"/> Catat Sisa Hutang — {panjar.number}</h3>
            <p className="text-xs text-slate-500 mt-1">Untuk biaya yang baru dibayar DP lewat panjar ini. Jurnal: Dr Beban / Cr Hutang Usaha 2101. Panjar & jurnal settle lama tidak berubah.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
        </div>
        <div className="w-48 mb-3">
          <Label>Tanggal Jurnal *</Label>
          <Input type="date" data-testid="remainder-date" value={date} onChange={(e) => setDate(e.target.value)}/>
        </div>
        <div className="grid grid-cols-12 gap-2 px-1 pb-1 text-[9px] uppercase tracking-wide font-bold text-slate-500">
          <div className="col-span-3">AWB</div><div className="col-span-4">Vendor</div><div className="col-span-2">Sisa Belum Dibayar</div><div className="col-span-2">Deskripsi</div><div className="col-span-1"></div>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`remainder-row-${i}`}>
              <div className="col-span-3">
                <SearchableSelect testid={`remainder-awb-${i}`} value={r.awb_id} onChange={(v) => upd(i, "awb_id", v)} options={options} placeholder="— Pilih AWB —" emptyText="AWB tidak ditemukan"/>
              </div>
              <div className="col-span-4">
                <VendorSelect testid={`remainder-vendor-${i}`} value={r.vendor_name} onChange={(v) => upd(i, "vendor_name", v)} vendors={vendors} onVendorsChange={loadVendors}/>
              </div>
              <div className="col-span-2"><NumberInput data-testid={`remainder-amount-${i}`} value={r.amount} onChange={(e) => upd(i, "amount", e.target.value)} placeholder="0"/></div>
              <div className="col-span-2"><Input data-testid={`remainder-desc-${i}`} value={r.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Mis. Pelunasan 30%"/></div>
              <div className="col-span-1 text-right">
                {rows.length > 1 && <button type="button" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14}/></button>}
              </div>
            </div>
          ))}
        </div>
        <button type="button" data-testid="remainder-add-row" onClick={() => setRows([...rows, emptyRow()])} className="mt-2 text-[11px] font-bold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"><Plus size={12}/> Tambah baris</button>
        <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 flex justify-between text-sm">
          <span>Total sisa jadi hutang vendor</span><b className="font-mono" data-testid="remainder-total">{fmtIDR(total)}</b>
        </div>
        <p className="text-[11px] text-rose-700 mt-2">Pastikan sisa ini belum pernah dibebankan lewat panjar/biaya lain supaya beban tidak dobel.</p>
        <div className="flex justify-end gap-2 pt-4 mt-2 border-t">
          <Button variant="secondary" onClick={onClose}>Batal</Button>
          <Button data-testid="remainder-save" disabled={saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan Sisa Hutang"}</Button>
        </div>
      </Card>
    </div>
  );
}
